import { html, nothing, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styles as globalStyles } from '~/ui/scss/main.styles';
import DERFormElement from '~/ui/utils/der-form-element';
import { DerToggleVariant } from './enum';
import { styles } from './tablist.styles';

export interface DerToggleChangeEvent {
    value: string | number | string[] | undefined;
}

export interface DerToggleOption {
    value: string | number | string[] | undefined;
    label: string;
    title?: string;
}

export class Tablist extends DERFormElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: Boolean })
    disabled = false;

    @property({ type: String })
    label?: string;

    @property({ type: String })
    name?: string;

    @property({ type: Boolean })
    required = false;

    @property({ type: Number, attribute: 't-index' })
    tIndex: number = 0;

    @property()
    value: string | number | undefined = undefined;

    @property({ type: String })
    variant: DerToggleVariant = DerToggleVariant.Default;

    @property({ type: Array })
    options: DerToggleOption[] = [];

    @property({ type: Boolean })
    valid?: boolean;

    @query('input') inputElement!: HTMLInputElement;

    @state()
    private inputId: string = `der-ui-tablist-${Math.random().toString(36).slice(2, 11)}`;
    private scrollTimeout?: number;
    private containerElement?: HTMLElement;

    constructor() {
        super();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
    }

    override connectedCallback(): void {
        super.connectedCallback();
        this._internals.role = 'radiogroup';
        this._internals.ariaRequired = this.required ? 'true' : 'false';
        this._internals.ariaInvalid = this._getAriaInvalid() || 'false';
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this.containerElement) {
            this.containerElement.removeEventListener('scroll', this.handleScroll);
        }
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }
    }

    private handleScroll = () => {
        // Debounce für bessere Performance
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }

        this.scrollTimeout = window.setTimeout(() => {
            this.updateScrollState();
        }, 16); // ~60fps
    };

    private updateScrollState() {
        if (!this.containerElement) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.containerElement;
        const isScrollable = scrollWidth > clientWidth;
        const tolerance = 1; // Pixel tolerance for rounding errors

        // Reset all classes
        this.containerElement.classList.remove('scroll-start', 'scroll-end', 'no-scroll');

        if (!isScrollable) {
            // Not scrollable - no fade
            this.containerElement.classList.add('no-scroll');
        } else if (scrollLeft <= tolerance) {
            // At the start - no fade left
            this.containerElement.classList.add('scroll-start');
        } else if (scrollLeft >= scrollWidth - clientWidth - tolerance) {
            // At the end - no fade right
            this.containerElement.classList.add('scroll-end');
        }
        // Middle position - both fades active (default)
    }

    override firstUpdated() {
        this.containerElement = this.shadowRoot?.querySelector('.c-tablist__container') as HTMLElement;

        if (this.containerElement) {
            // Scroll-Event-Listener hinzufügen
            this.containerElement.addEventListener('scroll', this.handleScroll);

            // Resize-Observer für responsive Änderungen
            const resizeObserver = new ResizeObserver(() => {
                this.updateScrollState();
            });
            resizeObserver.observe(this.containerElement);

            // Initiale Prüfung
            this.updateScrollState();
        }
        if (this.value) {
            this._setValue(this.value);
        }
        this._checkInputValidity();
    }

    setOptions(options: DerToggleOption[]): void {
        this.options = [...options];
    }

    private get _name(): string {
        return this.name || this.inputId;
    }

    private _onBlur = (ev: FocusEvent): void => {
        this.dispatchEvent(
            new CustomEvent('der.blur', {
                detail: ev,
                bubbles: true,
                composed: true,
            })
        );
    };

    private _onFocus = (ev: FocusEvent): void => {
        this.dispatchEvent(
            new CustomEvent('der.focus', {
                detail: ev,
                bubbles: true,
                composed: true,
            })
        );
    };

    private _setValue = (value: string | number): void => {
        const oldValue = this.value;
        this.value = value;

        // Explicitly request an update to ensure Lit detects the change
        this.requestUpdate('value', oldValue);
        this._internals.setFormValue(String(value));
        this._checkInputValidity();

        this.dispatchEvent(
            new CustomEvent('der.change', {
                detail: { value },
                bubbles: true,
                composed: true,
            })
        );
    };

    private _checkInputValidity() {
        if (!this.inputElement && !this.required) {
            return;
        }
        this.setDERValidity({ valueMissing: typeof this.value === 'undefined' }, 'Missing Value', this.inputElement);
    }

    // Alternative solution: event listener instead of onClick
    private _onChange = (e: Event): void => {
        if (this.disabled) {
            return;
        }

        const targetRadio = e.target as HTMLInputElement;
        if (targetRadio && targetRadio.type === 'radio' && targetRadio.checked) {
            const newValue = targetRadio.value;
            this._setValue(newValue);
        }
    };

    private _getAriaInvalid(): 'true' | 'false' | undefined {
        if (this.valid === true) {
            return 'false';
        } else if (this.valid === false) {
            return 'true';
        }
        return undefined;
    }

    override render() {
        const { options } = this;

        const inputClasses = {
            'c-tablist__input': true,
            'off-default': this.variant === DerToggleVariant.OffDefault,
            disabled: this.disabled,
            error: this.valid === false,
        };

        return html`
            <div class="c-tablist__container">
                <div
                    class=${classMap(inputClasses)}
                    role="radiogroup"
                    aria-required=${this.required}
                    aria-invalid=${this._getAriaInvalid()}
                    @blur=${this._onBlur}
                    @focus=${this._onFocus}
                >
                    ${options.length > 0
                        ? options.map(
                              (option, index) => html`
                                  <input
                                      type="radio"
                                      .checked=${option.value === this.value}
                                      tabindex=${this.tIndex}
                                      name=${this._name}
                                      id=${`radio-${this._name}-${index}`}
                                      value=${option.value}
                                      @change=${this._onChange}
                                  />
                                  <label
                                      class="c-tablist__group"
                                      tabindex="-1"
                                      for=${`radio-${this._name}-${index}`}
                                      aria-label=${option.title || nothing}
                                      title=${option.title || nothing}
                                  >
                                      <span class="c-tablist__label-inline">${option.label}</span>
                                  </label>
                              `
                          )
                        : nothing}
                </div>
            </div>
        `;
    }
}
